'use client';

import { isDoorstepDelivery } from '@coolblue-development/next-installation-services/client';
import { InsuranceProvider } from '@coolblue-development/next-insurance/insurance-selector';
import type { Accessory, ProductSummary } from '@features/add-to-cart-page/typings/Product';
import type {
  EnrichedShoppingCartItem,
  MappedShoppingCartItem
} from '@features/add-to-cart-page/typings/ShoppingCart';
import { getShoppingCartItemFromProductId } from '@features/add-to-cart-page/utils/shoppingCartUtils';
import getCrossSellCharger from '@utils/getCrossSellCharger';
import { createContext, useContext, useState } from 'react';

import { EnabledCrossSellChargerProductTypes } from '../../../../constants';
import {
  PromotionType,
  SummaryItem,
  SummaryItemPaymentType,
  SummaryItemType
} from './SummaryStore.types';

type SummaryContextApi = {
  items: SummaryItem[];
  addItem(item: SummaryItem): void;
  removeItem(id: SummaryItem['id']): void;
  mainProductQuantity: number;
  mainProductShoppingCartItem?: MappedShoppingCartItem;
  trackingSubFeature?: string;
};

const SummaryContext = createContext<SummaryContextApi | null>(null);

export function SummaryStoreProvider({
  accessories,
  children,
  isPaidDelivery = false,
  product,
  shoppingCartItems = [],
  trackingSubFeature
}: {
  accessories?: Accessory[];
  children: React.ReactNode;
  isPaidDelivery?: boolean;
  product?: ProductSummary;
  shoppingCartItems?: EnrichedShoppingCartItem[];
  trackingSubFeature?: string;
}) {
  const mainProductShoppingCartItem = getShoppingCartItemFromProductId(
    shoppingCartItems,
    product?.id
  );

  const getCrossSellChargerItem: () => SummaryItem[] = () => {
    const addedCharger = getCrossSellCharger(shoppingCartItems, mainProductShoppingCartItem);

    if (addedCharger?.length > 0) return addedCharger;

    const accessoryCharger = accessories?.find(accessory => {
      return EnabledCrossSellChargerProductTypes.includes(accessory.productTypeId);
    });

    if (accessoryCharger) return [accessoryCharger] as unknown as SummaryItem[];

    return [];
  };

  const [items, setItems] = useState<SummaryItem[]>([
    ...getShoppingCartFreeItems(),
    ...getNespressoPromotion(),
    ...getInsurances(),
    ...getInstallationServices(),
    ...getCrossSellChargerItem()
  ]);

  const mainProductQuantity = mainProductShoppingCartItem?.quantity ?? 1;

  function getShoppingCartFreeItems() {
    if (!mainProductShoppingCartItem || !product) {
      return [];
    }

    const hasFreeWithPromotion = product.productPlusPromotions?.some(
      promotion =>
        promotion.type === PromotionType.FreeWith || promotion.type === PromotionType.Norton
    );

    if (!hasFreeWithPromotion) {
      return [];
    }

    return (product.productPlusPromotions ?? []).map(promotion => ({
      id: promotion.id,
      name: promotion.title,
      quantity: 1,
      type: SummaryItemType.FreeWith,
      paymentType: SummaryItemPaymentType.OneTime,
      showPrice: true,
      salesPrice: {
        includingVat: 0,
        excludingVat: 0
      },
      listPrice: {
        includingVat: promotion.products[0].listPrice ?? 0,
        excludingVat: promotion.products[0].listPrice ?? 0
      }
    }));
  }

  function getNespressoPromotion() {
    if (!mainProductShoppingCartItem || !product) {
      return [];
    }

    const nespresssoPromotion = product.productPlusPromotions?.find(
      promotion => promotion.type === PromotionType.Nespresso
    );

    if (!nespresssoPromotion || nespresssoPromotion.products.length === 0) {
      return [];
    }

    const hasNespressoCupsProduct = mainProductShoppingCartItem.subItems.some(item =>
      nespresssoPromotion.products.some(product => product.id.toString() === item.productId)
    );

    if (!hasNespressoCupsProduct) {
      return [];
    }

    const nespressoCupsProduct = nespresssoPromotion.products[0];

    return [
      {
        id: nespressoCupsProduct.id,
        name: nespressoCupsProduct.name,
        quantity: mainProductShoppingCartItem.quantity,
        paymentType: SummaryItemPaymentType.OneTime,
        showPrice: true,
        salesPrice: {
          includingVat: nespressoCupsProduct.priceIncludingVat,
          excludingVat: nespressoCupsProduct.priceExcludingVat ?? 0
        },
        type: SummaryItemType.Promotion,
        ...(!!nespressoCupsProduct.listPrice && {
          listPrice: {
            includingVat: nespressoCupsProduct.listPrice,
            excludingVat: nespressoCupsProduct.listPrice
          }
        })
      }
    ];
  }

  function getInsurances() {
    if (!mainProductShoppingCartItem || !product || !mainProductShoppingCartItem.insurance) {
      return [];
    }

    const insurance = mainProductShoppingCartItem.insurance;

    const isRegularInsurance = insurance.provider === InsuranceProvider.CoverGenius;

    const getInsuranceName = isRegularInsurance
      ? product.partnerInsurance?.insuranceQuotes.find(
          insuranceProduct =>
            insuranceProduct.insuranceQuoteId === mainProductShoppingCartItem.insurance?.quoteId
        )?.title
      : product.insurance?.packages
          .flatMap(insurancePackage => insurancePackage.products)
          .find(insuranceProduct => insuranceProduct.id === insurance.productId)?.title;

    return [
      {
        id: isRegularInsurance ? insurance.quoteId : insurance.productId,
        name: getInsuranceName ?? '',
        quantity: mainProductShoppingCartItem.quantity,
        paymentType: SummaryItemPaymentType.OneTime,
        showPrice: insurance.price.includingVat > 0,
        salesPrice: {
          includingVat: insurance.price.includingVat,
          excludingVat: insurance.price.excludingVat
        },
        type: SummaryItemType.Insurance
      }
    ];
  }
  function getInstallationServices() {
    if (
      !mainProductShoppingCartItem ||
      mainProductShoppingCartItem?.installationServices?.length === 0 ||
      !product ||
      !product.installationServices
    ) {
      return [];
    }

    return (mainProductShoppingCartItem.installationServices ?? []).reduce<SummaryItem[]>(
      (list, item) => {
        const installationService = (product.installationServices ?? []).find(
          service => service.productId === item.id
        );

        if (installationService) {
          list.push({
            id: item.id,
            name: installationService.name,
            quantity: mainProductShoppingCartItem.quantity,
            paymentType: SummaryItemPaymentType.OneTime,
            showPrice: !(
              isPaidDelivery &&
              installationService.installServiceTypeId != null &&
              isDoorstepDelivery(installationService.installServiceTypeId)
            ),
            salesPrice: {
              includingVat: item.price.includingVat,
              excludingVat: item.price.excludingVat
            },
            type: SummaryItemType.Service
          });
        }

        return list;
      },
      []
    );
  }

  function addItem(item: SummaryItem) {
    setItems(state => {
      const existingItem = state.find(i => i.id === item.id);

      if (existingItem) {
        return state.map(i =>
          i.id === existingItem.id ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }

      return state.concat({ ...item, quantity: item.quantity || 1 });
    });
  }

  function removeItem(id: SummaryItem['id']) {
    setItems(state => state.filter(i => i.id !== id));
  }

  return (
    <SummaryContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        mainProductQuantity,
        mainProductShoppingCartItem,
        trackingSubFeature
      }}
    >
      {children}
    </SummaryContext.Provider>
  );
}

export function useSummaryStore() {
  const context = useContext(SummaryContext);

  if (!context) {
    throw new Error('useSummaryStore must be used within a SummaryStoreProvider');
  }

  return context;
}
